import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeAccessSection } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/components/employee-access-section';
import { EmployeeDetailsSection } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/components/employee-details-section';
import {
  EmployeeNotifications,
  RestaurantEmployee,
} from '@sales-portal/domain/accounts/screens/employee-list/types';
import { EmployeeNotificationsSection } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-notifications-section/employee-notifications-section';
import { SidePanel } from '@usepepper/sprout-kit-ui';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useUpdateEmployeeRoleMutator } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/blocs/mutators/use-update-employee-role-mutator';
import { useUpdateEmployeeShowPaymentsMutator } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/blocs/mutators/use-update-employee-show-payments-mutator';

interface EmployeeEditPanelContentProps {
  employee: RestaurantEmployee;
  notifications: EmployeeNotifications | null;
}

export const EmployeeEditPanelContent: React.FC<
  EmployeeEditPanelContentProps
> = ({ employee, notifications }) => {
  const [styles, theme] = useStyles();

  const {
    isAdmin,
    onChange: onAdminChange,
    pending: adminPending,
  } = useUpdateEmployeeRoleMutator(employee);
  const {
    showAppPayments,
    onChange: onShowPaymentsChange,
    pending: showPaymentsPending,
  } = useUpdateEmployeeShowPaymentsMutator(employee);

  return (
    <SidePanel.Body
      accessibilityLabel={i18n.t('sales_portal.employee_edit_drawer.title')}
      gap={theme.sk.spacing.spaceLarge}
      role="dialog"
    >
      <View style={styles.headerRow}>
        <Typography
          accessibilityRole="header"
          color={theme.sk.color.text.primary}
          variant="heading-md"
        >
          {i18n.t('sales_portal.employee_edit_drawer.title')}
        </Typography>
      </View>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <EmployeeDetailsSection employee={employee} />
        <EmployeeAccessSection
          adminPending={adminPending}
          isAdmin={isAdmin}
          onAdminChange={onAdminChange}
          onShowPaymentsChange={onShowPaymentsChange}
          showAppPayments={showAppPayments}
          showPaymentsPending={showPaymentsPending}
        />
        {!notifications ? (
          <ActivityIndicator accessibilityLabel={i18n.t('loading')} />
        ) : (
          <EmployeeNotificationsSection notifications={notifications} />
        )}
      </ScrollView>
    </SidePanel.Body>
  );
};

const useStyles = createStyles((theme) => ({
  headerRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  scrollContent: {
    gap: theme.sk.spacing.spaceMedium,
  },
}));
